import { ReactNode, useEffect, useMemo, useState } from "react";
import RoleLabel from "admin/components/user-management/RoleLabel";
import TagLabel from "admin/components/user-management/TagLabel";
import { UserDrawer } from "admin/components/user-management/UserDrawer";
import useCanEditAdminApp from "admin/hooks/useCanEditAdminApp";
import useGroups from "admin/hooks/useGroups";
import useRoles from "admin/hooks/useRoles";
import useUsers from "admin/hooks/useUsers";
import Role from "admin/types/interfaces/role";
import User, { UserGroup } from "admin/types/interfaces/user";
import sortRoles from "admin/utils/roles";
import { excludeSystemTags } from "admin/utils/tags";
import { ArrowDown, ArrowUp, ArrowUpDown, Search, X, AlertCircle } from "lucide-react";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import MultiSelect from "~/components/ui/multi-select";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "~/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "~/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";

const PAGE_SIZE_OPTIONS = [20, 50, 100] as const;
const SKELETON_ROWS = 20;

export const UserStatusFilter = {
  All: "all",
  Enabled: "enabled",
  Disabled: "disabled",
} as const;
export type UserStatusFilter = (typeof UserStatusFilter)[keyof typeof UserStatusFilter];

export const getSortIcon = (
  column: string,
  sortColumn: string | null,
  sortDirection: "asc" | "desc"
) => {
  if (sortColumn !== column) {
    return <ArrowUpDown className="h-4 w-4 text-muted-foreground/50" />;
  }

  return sortDirection === "asc" ? (
    <ArrowUp className="h-4 w-4" />
  ) : (
    <ArrowDown className="h-4 w-4" />
  );
};

const COLUMNS = [
  {
    id: "checkbox",
    label: "",
    className: "w-[24px]",
    isSortable: false,
    loadingClassName: "!w-4",
  },
  {
    id: "name",
    label: "Name",
    isSortable: true,
    className: "w-[180px] min-w-[140px]",
    loadingClassName: "max-w-40",
  },
  {
    id: "email",
    label: "Email",
    isSortable: true,
    className: "w-[200px] min-w-[160px]",
    loadingClassName: "max-w-56",
  },
  {
    id: "status",
    label: "Status",
    isSortable: true,
    isCentered: true,
    className: "w-[100px] min-w-[90px]",
    loadingClassName: "max-w-16 rounded-full mx-auto",
  },
  {
    id: "roles",
    label: "Roles",
    isSortable: false,
    className: "w-[140px] min-w-[110px]",
    loadingClassName: "max-w-20 rounded-full",
  },
  {
    id: "tags",
    label: "Groups",
    isSortable: false,
    className: "w-[140px] min-w-[110px]",
    loadingClassName: "max-w-12 rounded-full",
  },
  {
    id: "lastLogin",
    label: "Last Login",
    isSortable: true,
    className: "w-[110px] min-w-[100px]",
    loadingClassName: "max-w-20",
  },
  {
    id: "created",
    label: "Created",
    isSortable: true,
    className: "w-[110px] min-w-[100px]",
    loadingClassName: "max-w-20",
  },
];

export const getStatusBadgeVariant = (status: boolean) => {
  return status ? "success" : "muted";
};

const LoadingElement = ({ loadingClassName }: { loadingClassName: string }) => {
  return <div className={`h-4 bg-muted animate-pulse rounded w-full ${loadingClassName}`} />;
};

const UserTableLoadingSkeleton = () => {
  return (
    <>
      {Array.from({ length: SKELETON_ROWS }).map((_, index) => (
        <TableRow key={`loading-${index}`}>
          {COLUMNS.map((column) => (
            <TableCell key={column.id} className={column.className}>
              <LoadingElement loadingClassName={column.loadingClassName} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
};

const CountWithTooltip = ({
  singularLabel,
  pluralLabel,
  children,
}: {
  singularLabel: string;
  pluralLabel: string;
  children: ReactNode[];
}) => {
  if (children.length === 0) {
    return <span className="text-muted-foreground text-sm">None</span>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help whitespace-nowrap text-sm">
          {children.length} {children.length === 1 ? singularLabel : pluralLabel}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <div className="flex flex-col gap-1">{children}</div>
      </TooltipContent>
    </Tooltip>
  );
};

const UserTableRow = ({
  selected,
  user,
  handleRowClick,
  onToggleSelect,
  allRoles,
}: {
  selected: boolean;
  user: User;
  handleRowClick: (user: User) => void;
  onToggleSelect: (email: string) => void;
  allRoles?: Role[];
}) => {
  const getTagDisplayName = (tag: UserGroup) => tag.display_name || tag.name;

  const sortedRoles = [...user.roles].sort(sortRoles);
  const sortedTags = [...excludeSystemTags(user.tags)].sort((a, b) =>
    getTagDisplayName(a).localeCompare(getTagDisplayName(b))
  );

  const getIsInternalRole = (roleId: string) => {
    return allRoles?.find((role) => role.id === roleId)?.is_internal ?? false;
  };

  return (
    <TableRow
      key={user.email}
      className={`cursor-pointer hover:bg-muted/50 ${selected ? "bg-accent/50" : ""}`}
      onClick={() => handleRowClick(user)}
    >
      <TableCell onClick={(e) => e.stopPropagation()} className="text-center">
        <Checkbox
          checked={selected}
          onCheckedChange={() => onToggleSelect(user.email)}
          aria-label={`Select ${user.email}`}
        />
      </TableCell>
      <TableCell className={`font-medium ${COLUMNS[1].className}`}>
        <div className="truncate" title={user.full_name || "Unknown User"}>
          {user.full_name || "Unknown User"}
        </div>
      </TableCell>
      <TableCell className={COLUMNS[2].className}>
        <div className="truncate" title={user.email}>
          {user.email}
        </div>
      </TableCell>
      <TableCell className={`text-center pl-0 ${COLUMNS[3].className}`}>
        <Badge variant={getStatusBadgeVariant(user.is_active)}>
          {user.is_active ? "Enabled" : "Disabled"}
        </Badge>
      </TableCell>
      <TableCell className={COLUMNS[4].className}>
        <CountWithTooltip singularLabel="role" pluralLabel="roles">
          {sortedRoles.map((role) => (
            <RoleLabel key={role.id} roleName={role.name} isInternal={getIsInternalRole(role.id)} />
          ))}
        </CountWithTooltip>
      </TableCell>
      <TableCell className={COLUMNS[5].className}>
        <CountWithTooltip singularLabel="group" pluralLabel="groups">
          {sortedTags.map((tag) => (
            <TagLabel key={tag.name} tagName={getTagDisplayName(tag)} />
          ))}
        </CountWithTooltip>
      </TableCell>
      <TableCell className={COLUMNS[6].className}>
        <div className="truncate">
          {user.auth0?.last_login ? (
            <span className="text-sm">{new Date(user.auth0?.last_login).toLocaleDateString()}</span>
          ) : (
            <span className="text-muted-foreground text-sm">Never</span>
          )}
        </div>
      </TableCell>
      <TableCell className={COLUMNS[7].className}>
        <div className="truncate">
          {user.provisioned_at ? (
            new Date(user.provisioned_at).toLocaleDateString()
          ) : (
            <span className="text-muted-foreground text-sm">Unknown</span>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

const UserTable = ({
  selectedUsers,
  setSelectedUsers,
  onBulkEdit,
}: {
  selectedUsers: User[];
  setSelectedUsers: (users: User[]) => void;
  onBulkEdit: () => void;
}) => {
  const { data: users = [], isLoading, error: usersError } = useUsers();

  const { data: roles } = useRoles();
  const { data: tags = [] } = useGroups();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<UserStatusFilter>(UserStatusFilter.Enabled);
  const [roleFilter, setRoleFilter] = useState<string[]>([]);
  const [tagFilter, setTagFilter] = useState<string[]>([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedUserForDrawer, setSelectedUserForDrawer] = useState<User | null>(null);

  const canEdit = useCanEditAdminApp();

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, roleFilter, tagFilter]);

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const matchesSearch =
        user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        false ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === UserStatusFilter.All ||
        (user.is_active && statusFilter === UserStatusFilter.Enabled) ||
        (!user.is_active && statusFilter === UserStatusFilter.Disabled);

      let matchesRoles = true;
      if (roleFilter.length > 0) {
        const userRoles = user.roles.map((role) => role.id);
        matchesRoles = roleFilter.some((filter) => userRoles.includes(filter));
      }

      let matchesTags = true;
      if (tagFilter.length > 0) {
        const userTags = user.tags.map((tag) => tag.name);
        matchesTags = tagFilter.some((tag) => userTags.includes(tag));
      }

      return matchesSearch && matchesStatus && matchesRoles && matchesTags;
    });
  }, [roleFilter, searchTerm, statusFilter, tagFilter, users]);

  const sortedUsers = useMemo(
    () =>
      [...(filteredUsers ?? [])].sort((a, b) => {
        if (!sortColumn) {
          return 0;
        }

        let aValue: any;
        let bValue: any;

        switch (sortColumn) {
          case "name":
            aValue = a.full_name?.toLowerCase() || "";
            bValue = b.full_name?.toLowerCase() || "";
            break;
          case "email":
            aValue = a.email.toLowerCase();
            bValue = b.email.toLowerCase();
            break;
          case "status":
            aValue = a.is_active;
            bValue = b.is_active;
            break;
          case "lastLogin":
            aValue = a.auth0?.last_login ? new Date(a.auth0.last_login).getTime() : 0;
            bValue = b.auth0?.last_login ? new Date(b.auth0.last_login).getTime() : 0;
            break;
          case "created":
            aValue = a.provisioned_at ? new Date(a.provisioned_at).getTime() : 0;
            bValue = b.provisioned_at ? new Date(b.provisioned_at).getTime() : 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortDirection === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortDirection === "asc" ? 1 : -1;
        }
        return 0;
      }),
    [filteredUsers, sortColumn, sortDirection]
  );

  const totalPages = Math.ceil(sortedUsers.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedUsers = sortedUsers.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, Math.max(1, Math.ceil(sortedUsers.length / pageSize))));
  }, [pageSize, sortedUsers.length]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const getSortHeaderClasses = (column: (typeof COLUMNS)[number]) => {
    const baseClasses = `flex items-center gap-2 cursor-pointer hover:text-foreground transition-colors select-none group ${
      column.isCentered ? "justify-center" : ""
    }`;
    return sortColumn === column.id
      ? `${baseClasses} text-foreground`
      : `${baseClasses} text-muted-foreground`;
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const generatePaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        items.push(
          <PaginationItem key={i}>
            <PaginationLink
              onClick={() => handlePageChange(i)}
              isActive={currentPage === i}
              className="cursor-pointer"
            >
              {i}
            </PaginationLink>
          </PaginationItem>
        );
      }
    } else {
      items.push(
        <PaginationItem key={1}>
          <PaginationLink
            onClick={() => handlePageChange(1)}
            isActive={currentPage === 1}
            className="cursor-pointer"
          >
            1
          </PaginationLink>
        </PaginationItem>
      );

      if (currentPage > 3) {
        items.push(
          <PaginationItem key="ellipsis1">
            <PaginationEllipsis />
          </PaginationItem>
        );
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        items.push(
          <PaginationItem key={i}>
            <PaginationLink
              onClick={() => handlePageChange(i)}
              isActive={currentPage === i}
              className="cursor-pointer"
            >
              {i}
            </PaginationLink>
          </PaginationItem>
        );
      }

      if (currentPage < totalPages - 2) {
        items.push(
          <PaginationItem key="ellipsis2">
            <PaginationEllipsis />
          </PaginationItem>
        );
      }

      if (totalPages > 1) {
        items.push(
          <PaginationItem key={totalPages}>
            <PaginationLink
              onClick={() => handlePageChange(totalPages)}
              isActive={currentPage === totalPages}
              className="cursor-pointer"
            >
              {totalPages}
            </PaginationLink>
          </PaginationItem>
        );
      }
    }

    return items;
  };
  const handleRowClick = (user: User) => {
    setSelectedUserForDrawer(user);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedUserForDrawer(null);
  };

  const pageEmails = paginatedUsers.map((u) => u.email);
  const allPageSelected =
    pageEmails.length > 0 &&
    pageEmails.every((email) => selectedUsers.some((user) => user.email === email));

  const someSelected = selectedUsers.length > 0;

  const handleSelectAllPage = () => {
    if (allPageSelected) {
      setSelectedUsers(selectedUsers.filter((u) => !pageEmails.includes(u.email)));
    } else {
      setSelectedUsers([
        ...selectedUsers.filter((u) => !pageEmails.includes(u.email)),
        ...paginatedUsers,
      ]);
    }
  };

  const handleClearSelection = () => {
    setSelectedUsers([]);
  };

  const handleToggleSelect = (email: string) => {
    const next = [...selectedUsers];
    if (next.some((u) => u.email === email)) {
      setSelectedUsers(next.filter((u) => u.email !== email));
    } else {
      const user = users.find((u) => u.email === email);
      if (user) setSelectedUsers([...next, user]);
    }
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-10 h-9 border border-border bg-background"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="relative">
            <Select
              value={statusFilter}
              onValueChange={(value) => setStatusFilter(value as UserStatusFilter)}
            >
              <SelectTrigger className="w-32 h-9 border border-border bg-background">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UserStatusFilter.All}>All Statuses</SelectItem>
                <SelectItem value={UserStatusFilter.Enabled}>Enabled</SelectItem>
                <SelectItem value={UserStatusFilter.Disabled}>Disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {roles && (
            <MultiSelect
              options={roles.map((role) => ({
                value: role.id,
                label: role.name,
              }))}
              value={roleFilter}
              onValueChange={setRoleFilter}
              placeholder="Roles"
              className="w-48 [&>button]:border-border [&>button]:h-9 [&>button]:bg-background"
            />
          )}

          <MultiSelect
            options={
              excludeSystemTags(tags).map((tag) => ({
                value: tag.name,
                label: tag.display_name || tag.name,
              })) || []
            }
            value={tagFilter}
            onValueChange={setTagFilter}
            placeholder="Groups"
            className="w-48 [&>button]:border-border [&>button]:h-9 [&>button]:bg-background"
          />
        </div>
      </div>

      {sortedUsers.length > 0 && (
        <div className="flex items-center gap-2 ml-2 h-3">
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <Checkbox
              checked={allPageSelected ? true : someSelected ? "indeterminate" : false}
              onCheckedChange={handleSelectAllPage}
            />
            <span>{someSelected ? `${selectedUsers.length} selected users` : "Select"}</span>
          </label>
          {selectedUsers.length > 1 && canEdit && (
            <Button variant="ghost" size="sm" onClick={onBulkEdit} className="h-8">
              Bulk Edit
            </Button>
          )}
          {someSelected && (
            <Button variant="ghost" size="sm" onClick={handleClearSelection} className="h-8">
              Clear selection
            </Button>
          )}
        </div>
      )}

      {usersError && (
        <div className="p-3 border border-destructive/20 bg-destructive/10 text-destructive rounded-md text-sm flex items-center gap-2 mb-4">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>Unable to load users</span>
        </div>
      )}

      <div className="rounded-lg border overflow-x-auto">
        <Table className="table-fixed w-full">
          <TableHeader>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableHead
                  key={column.id}
                  className={`${column.className ?? ""} ${
                    column.isSortable ? "cursor-pointer" : ""
                  } ${column.isCentered ? "text-center" : ""}`}
                  {...(column.isSortable && {
                    onClick: () => handleSort(column.id),
                  })}
                >
                  {column.isSortable ? (
                    <div className={getSortHeaderClasses(column)}>
                      <span>{column.label}</span>
                      <div
                        className={`transition-opacity ${
                          sortColumn === column.id
                            ? "opacity-100"
                            : "opacity-0 group-hover:opacity-100"
                        }`}
                      >
                        {getSortIcon(column.id, sortColumn, sortDirection)}
                      </div>
                    </div>
                  ) : (
                    column.label
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <UserTableLoadingSkeleton />
            ) : paginatedUsers.length === 0 ? (
              <TableRow className="pointer-events-none">
                <TableCell
                  colSpan={8} // checkbox, name, email, status, roles, tags, last login, created
                  className="text-center text-muted-foreground py-8"
                >
                  No users found{" "}
                  {roleFilter.length > 0 || tagFilter.length > 0
                    ? "matching your criteria"
                    : searchTerm && `matching "${searchTerm}"`}
                </TableCell>
              </TableRow>
            ) : (
              paginatedUsers.map((user) => (
                <UserTableRow
                  key={user.email}
                  user={user}
                  selected={selectedUsers.some((u) => u.email === user.email)}
                  handleRowClick={handleRowClick}
                  onToggleSelect={handleToggleSelect}
                  allRoles={roles}
                />
              ))
            )}
          </TableBody>
        </Table>
        {selectedUserForDrawer && (
          <UserDrawer
            isOpen={isDrawerOpen}
            onClose={handleCloseDrawer}
            user={selectedUserForDrawer}
          />
        )}
      </div>

      {sortedUsers.length > 0 && (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <p className="text-sm text-muted-foreground text-nowrap">
              Showing {startIndex + 1} to {Math.min(endIndex, sortedUsers.length)} of{" "}
              {sortedUsers.length} users
            </p>
            <Select value={String(pageSize)} onValueChange={(value) => setPageSize(Number(value))}>
              <SelectTrigger className="w-[72px] h-8 border border-border bg-background" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">per page</span>
          </div>

          {totalPages > 1 && (
            <div>
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                      className={
                        currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"
                      }
                    />
                  </PaginationItem>

                  {generatePaginationItems()}

                  <PaginationItem>
                    <PaginationNext
                      onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                      className={
                        currentPage === totalPages
                          ? "pointer-events-none opacity-50"
                          : "cursor-pointer"
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default UserTable;
